import { Injectable, inject } from "@angular/core";
import { MITPService } from "./mitp.service";
import { HttpClient, HttpResponse } from "@angular/common/http";
import { Observable } from "rxjs";
import { Institute } from "../interfaces/institute";
import { ISubscription } from "../interfaces/isubscription";
import { ESyncType } from "../enums/esync-type";
import { InstituteType } from "../interfaces/institute-type";
import { IFiness } from "../interfaces/institutes/ifiness";
import { ICountry } from "../interfaces/institutes/icountry";
import { InstituteCredentials } from "../interfaces/institute-credentials";
import { EQualityCircuit } from "../modules/quality/enums/equality-circuit";
import { InstituteUsageStorage } from "../modules/institutes/institute-usage-storage/institute-usage-storage";

@Injectable({
	providedIn: "root",
})
export class InstituteService {
	private mitp = inject(MITPService);
	private httpClient = inject(HttpClient);

	getInstitutesGeo(): Observable<Institute[]> {
		return this.httpClient.get<Institute[]>(this.mitp.Institutes() + "/geo");
	}

	addSubscription(institute: Institute, subscription: ISubscription): Observable<HttpResponse<null>> {
		return this.httpClient.post<null>(this.mitp.Institutes() + `/${institute.id}/subscriptions`, subscription, {
			observe: "response",
		});
	}

	isSecurity(): Observable<{ security: boolean }> {
		return this.httpClient.get<{ security: boolean }>(this.mitp.Institutes() + `/parameters/security`);
	}

	editSyncType(instituteId: number, type: ESyncType): Observable<HttpResponse<null>> {
		return this.httpClient.put<null>(
			this.mitp.Institutes() + `/${instituteId}/parameters/sync-type/${type}`,
			null,
			{
				observe: "response",
			}
		);
	}

	editQualityCircuit(instituteId: number, type: EQualityCircuit): Observable<HttpResponse<null>> {
		return this.httpClient.put<null>(
			this.mitp.Institutes() + `/${instituteId}/parameters/quality-circuit/${type}`,
			null,
			{
				observe: "response",
			}
		);
	}

	getInstitutes() {
		return this.httpClient.get<Institute[]>(this.mitp.Institutes());
	}

	getInstitute(id: number): Observable<Institute> {
		return this.httpClient.get<Institute>(this.mitp.Institutes() + `/${id}`);
	}

	createInstitute(institute: any) {
		return this.httpClient.post<Institute>(this.mitp.Institutes(), institute);
	}

	getShareableInstitutes() {
		return this.httpClient.get<Institute[]>(this.mitp.Institutes() + "/shareable");
	}

	getInstituteTypes() {
		return this.httpClient.get<InstituteType[]>(this.mitp.Institutes() + "/type");
	}

	getInstituteStatistics(instituteId: number) {
		return this.httpClient.get(this.mitp.Statistics() + `/institutes/${instituteId}`);
	}

	getInstituteHeader(instituteId: number, isFooter: boolean = false): Observable<string> {
		const type = isFooter ? "footer" : "header";
		return this.httpClient.get<string>(this.mitp.Institutes() + `/${instituteId}/${type}`);
	}

	createInstituteType(label: string) {
		return this.httpClient.post<InstituteType>(this.mitp.Institutes() + "/type", { label: label });
	}

	getInstituteLogo(instituteId: number) {
		return this.httpClient.get(this.mitp.Institutes() + `/${instituteId}/logo`, { responseType: "blob" });
	}

	updateInstitute(instituteId: number, institute: any) {
		return this.httpClient.put(this.mitp.Institutes() + `/${instituteId}`, institute, { observe: "response" });
	}

	updateInstituteParameters(instituteId: number, params: any) {
		return this.httpClient.put(this.mitp.Institutes() + `/${instituteId}/params`, params, { observe: "response" });
	}

	updateInstituteHeader(
		instituteId: number,
		header: string,
		isFooter: boolean = false
	): Observable<HttpResponse<null>> {
		const type = isFooter ? "footer" : "header";
		return this.httpClient.put<null>(
			this.mitp.Institutes() + `/${instituteId}/${type}`,
			{ header: header },
			{ observe: "response" }
		);
	}

	updateInstituteAddress(instituteId: number, address): Observable<HttpResponse<null>> {
		return this.httpClient.put<null>(this.mitp.Institutes() + `/${instituteId}/address`, address, {
			observe: "response",
		});
	}

	searchInstituteByFiness(finess: string): Observable<IFiness> {
		return this.httpClient.get<IFiness>(this.mitp.Institutes() + `/search/finess/${finess}`);
	}

	searchInstituteBySiret(siret: string): Observable<IFiness> {
		return this.httpClient.get<IFiness>(this.mitp.Institutes() + `/search/siret/${siret}`);
	}

	getCountries(): Observable<ICountry[]> {
		return this.httpClient.get<ICountry[]>(this.mitp.Institutes() + "/countries");
	}

	getProtocolTemplate(instituteId: number): Observable<string> {
		return this.httpClient.get<string>(this.mitp.Institutes() + `/${instituteId}/protocol-template`);
	}

	setProtocolTemplate(instituteId: number, template: string): Observable<HttpResponse<null>> {
		return this.httpClient.put<null>(
			this.mitp.Institutes() + `/${instituteId}/protocol-template`,
			{ template: template },
			{ observe: "response" }
		);
	}

	getInstituteCredentials(instituteId: number): Observable<InstituteCredentials> {
		return this.httpClient.get<InstituteCredentials>(this.mitp.Institutes() + `/${instituteId}/credentials`);
	}

	setInstituteCredentials(instituteId: number, credentials: FormData): Observable<HttpResponse<null>> {
		return this.httpClient.put<null>(this.mitp.Institutes() + `/${instituteId}/credentials`, credentials, {
			observe: "response",
		});
	}

	addIpAddress(instituteId: number, ipAddress: string): Observable<HttpResponse<null>> {
		return this.httpClient.post<null>(
			this.mitp.Institutes() + `/${instituteId}/parameters/ip`,
			{ ip: ipAddress },
			{
				observe: "response",
			}
		);
	}

	deleteIpAddress(instituteId: number, ipAddress: string): Observable<HttpResponse<null>> {
		return this.httpClient.delete<null>(this.mitp.Institutes() + `/${instituteId}/parameters/ip/${ipAddress}`, {
			observe: "response",
		});
	}

	editGlobalCodification(
		instituteId: number,
		enabled: boolean,
		globalCodification: string
	): Observable<HttpResponse<null>> {
		return this.httpClient.put<null>(
			this.mitp.Institutes() + `/${instituteId}/parameters/codification`,
			{
				enabled: enabled,
				codification: globalCodification,
			},
			{
				observe: "response",
			}
		);
	}

	editCustomUrl(instituteId: number, customUrl: string, enableWhiteLabel: boolean): Observable<HttpResponse<null>> {
		return this.httpClient.put<null>(
			this.mitp.Institutes() + `/${instituteId}/parameters/custom-url`,
			{ customUrl: customUrl, enableWhiteLabel: enableWhiteLabel },
			{
				observe: "response",
			}
		);
	}

	editEncountersCodification(
		instituteId: number,
		enabled: boolean,
		encountersCodification: string
	): Observable<HttpResponse<null>> {
		return this.httpClient.put<null>(
			this.mitp.Institutes() + `/${instituteId}/parameters/encounter-codification`,
			{
				enabled: enabled,
				codification: encountersCodification,
			},
			{
				observe: "response",
			}
		);
	}

	getUsageStorage(instituteId: number): Observable<InstituteUsageStorage> {
		return this.httpClient.get<InstituteUsageStorage>(this.mitp.Institutes() + `/${instituteId}/storage`);
	}
}
